import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Collapse,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useDebouncedInput } from '@/hooks/useDebouncedInput';
import styles from './ChallengesSearchBar.module.scss';

interface ChallengesSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  // Filter expansion (for future regional filters)
  showFiltersButton?: boolean;
  filtersExpanded?: boolean;
  onToggleFilters?: () => void;
  // Optional additional filters slot
  additionalFilters?: React.ReactNode;
}

/**
 * Clean search bar component for Challenges page
 * Features:
 * - Debounced text input with smooth typing
 * - Clear button when there's content
 * - Optional filter expansion button for future filters
 * - Minimal, clean design
 */
const ChallengesSearchBar: React.FC<ChallengesSearchBarProps> = ({
  value,
  onChange,
  placeholder = 'Buscar por finca, agricultor, cédula, teléfono, región o grupo...',
  showFiltersButton = false,
  filtersExpanded = false,
  onToggleFilters,
  additionalFilters,
}) => {
  const { localValue, handleChange, handleClear } = useDebouncedInput({
    value,
    onChange,
  });

  return (
    <Box className={styles.container}>
      <Box className={styles.searchRow}>
        <TextField
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          size="small"
          fullWidth
          className={styles.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={styles.searchIcon} />
              </InputAdornment>
            ),
            endAdornment: localValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                  aria-label="Limpiar búsqueda"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              borderRadius: '8px',
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#bdbdbd',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />

        {showFiltersButton && onToggleFilters && (
          <Button
            variant={filtersExpanded ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={onToggleFilters}
            className={styles.filterButton}
            size="small"
          >
            Filtros
          </Button>
        )}
      </Box>

      {/* Helper text */}
      <Box className={styles.helperText}>
        La búsqueda se aplica después de 1 segundo
      </Box>

      {/* Additional filters (expandable) */}
      {showFiltersButton && additionalFilters && (
        <Collapse in={filtersExpanded}>
          <Paper className={styles.filtersContainer} elevation={0}>
            {additionalFilters}
          </Paper>
        </Collapse>
      )}
    </Box>
  );
};

export default ChallengesSearchBar;
